import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Banner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // ✅ FIRST SLIDE = Drone Manufacturing (LCP)
  const slides = [
    {
      id: 0,
      title: "Drone Manufacturing",
      subtitle: "Chase the View",
      description: "Dual-Camera Drone for Travel Photography",
      image: "/images/drone/b2.avif", // ✅ LCP IMAGE
    },
    {
      id: 1,
      title: "Drone Training",
      subtitle: "Capture Beyond",
      description: "Professional Aerial Imaging System",
      image: "/images/drone/b3.avif",
    },
    {
      id: 2,
      title: "Drone As Defence",
      subtitle: "Create With Clarity",
      description: "Stabilized Pocket Camera",
      image: "/images/drone/b4.avif",
    },
    {
      id: 3,
      title: "Agri Drone",
      subtitle: "Farm From Above",
      description: "Precision Agriculture Mapping & Spraying",
      image: "/images/drone/b1.avif",
    },
  ];

  const animationDuration = 600;

  const nextSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      const nextIndex =
        currentSlide === slides.length - 1 ? 0 : currentSlide + 1;

      setTimeout(() => {
        setCurrentSlide(nextIndex);
        setIsAnimating(false);
      }, animationDuration);
    }
  }, [currentSlide, isAnimating, slides.length]);

  const prevSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      const prevIndex =
        currentSlide === 0 ? slides.length - 1 : currentSlide - 1;

      setTimeout(() => {
        setCurrentSlide(prevIndex);
        setIsAnimating(false);
      }, animationDuration);
    }
  }, [currentSlide, isAnimating, slides.length]);

  useEffect(() => {
    const timer = setTimeout(nextSlide, 5000);
    return () => clearTimeout(timer);
  }, [currentSlide, nextSlide]);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* ✅ OPTIMIZED IMAGE RENDERING */}
      <div className="absolute inset-0 w-full h-full" style={{ willChange: "opacity" }}>
        {slides.map((slide, index) => {
          const isActive = currentSlide === index;
          const isNext = (currentSlide + 1) % slides.length === index;

          // ✅ Only render required images in the DOM for performance
          // Always keep index 0 for LCP stability, active slide, and next slide for preloading
          if (!isActive && !isNext && index !== 0) return null;

          return (
            <img
              key={slide.id}
              src={slide.image}
              alt={slide.title}
              width="1280"
              height="720"
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "low"}
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${isActive ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
            />
          );
        })}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 z-20"></div>

      {/* Content */}
      <div className="relative z-20 h-full flex flex-col justify-center items-center text-center px-4">
        <p className="text-white/80 text-sm mb-2">
          {slides[currentSlide].description}
        </p>

        <h1 className="text-white text-3xl md:text-6xl font-semibold mb-2">
          {slides[currentSlide].title}
        </h1>

        <p className="text-white/90 text-lg md:text-2xl">
          {slides[currentSlide].subtitle}
        </p>
      </div>

      {/* Navigation */}
      <button
        onClick={prevSlide}
        aria-label="Previous slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 p-3 rounded-full text-white"
      >
        <ChevronLeft />
      </button>

      <button
        onClick={nextSlide}
        aria-label="Next slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 p-3 rounded-full text-white"
      >
        <ChevronRight />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`h-2 rounded-full ${currentSlide === index
              ? "w-6 bg-white"
              : "w-3 bg-white/50"
              }`}
          />
        ))}
      </div>
    </div>
  );
};

export default Banner;